import PrimaryTabs, { PrimaryTabItem } from '@/components/core/primary-tabs';
import { PortfoliosErrorModal } from '@/components/error/portfolios-error-modal';
import Loader from '@/components/loader';
import { Pages } from '@/constants/pages';
import { setErrorModal } from '@/services/fetchers';
import { useHome } from '@/store/useHome';
import { useGetTradingMarketStatus } from '@/store/withdrawal/usePlaceWithdrawOrder';
import { Alert, AlertTitle, Box, Container } from '@mui/material';
import { isEmpty, startsWith } from 'lodash';
import { useRouter } from 'next/router';
import {
  PropsWithChildren,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useBranding } from '../store/branding/useBranding';
import { getBrandFromHostName } from '../utils/get-brand-from-host-name';

export type PrimaryLayoutProps = {
  title?: ReactNode;
  onClickBack?: () => void;
  isLoading?: boolean;
  isTabsVisible?: boolean;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  primaryLayoutRef?: RefObject<HTMLDivElement | null>;
};

const PrimaryLayout = ({
  children,
  onClickBack,
  title,
  isLoading = false,
  isTabsVisible = false,
  primaryLayoutRef,
  onScroll,
}: PropsWithChildren<PrimaryLayoutProps>) => {
  const { userPortfolios, businessPortfolios } = useHome();
  const { data } = useGetTradingMarketStatus();
  const router = useRouter();
  const [error, setError] = useState<{ message: string } | null>(null);

  /* If user doesn't have any active portfolios, they are fresh users */
  const isFreshUser = isEmpty(userPortfolios);
  const brand = getBrandFromHostName();
  const { data: config } = useBranding(brand);

  const { pathname } = useRouter();

  const tabItems = useMemo(
    () =>
      [
        {
          title: 'Home',
          href: Pages.home,
          selected: pathname === '/',
        },
        {
          title: 'Explore',
          href: Pages.subscribe,
          disabled: isEmpty(businessPortfolios),
          selected:
            startsWith(pathname, Pages.subscribe) &&
            !startsWith(pathname, Pages.newSubscription),
        },
        {
          title: 'Track',
          href: Pages.invest,
          disabled: isFreshUser,
          selected:
            startsWith(pathname, Pages.invest) ||
            startsWith(pathname, Pages.withdraw) ||
            startsWith(pathname, Pages.newSubscription),
        },
        {
          title: 'Rebalance',
          href: Pages.rebalance,
          disabled: isFreshUser,
          selected: startsWith(pathname, Pages.rebalance),
        },
        {
          title: 'Learn',
          href: Pages.learn,
          selected: startsWith(pathname, Pages.learn),
        },
      ] as PrimaryTabItem[],
    [businessPortfolios, isFreshUser, pathname],
  );

  useEffect(() => {
    setErrorModal((error) => {
      setError({ message: error.message });
    });
  }, []);

  const handleRetry = () => {
    setError(null);
    router.reload();
  };

  return (
    <Container
      ref={primaryLayoutRef}
      onScroll={onScroll}
      sx={{
        px: 0,
        margin: 'auto',
        overflowY: 'auto',
        maxHeight: '100vh',
      }}
    >
      {!isTabsVisible ? <PrimaryTabs items={tabItems} /> : <></>}
      {data && !data?.status && (
        <Box m={1}>
          <Alert
            severity='error'
            variant='filled'
            icon={false}
            sx={(theme) => ({
              backgroundColor: theme.palette.custom.performance.negative,
            })}
          >
            {data?.title && <AlertTitle>{data?.title}</AlertTitle>}
            {data?.message}
          </Alert>
        </Box>
      )}
      <Box
        sx={{
          minHeight: 'calc(100dvh - 100px)',
          px: '8px',
          ...(data && data?.status ? { py: '30px' } : { pb: '30px', pt: 0 }),
          position: 'relative',
        }}
      >
        {isLoading ? <Loader /> : children}
      </Box>
      {error && <PortfoliosErrorModal onRetry={handleRetry} />}
    </Container>
  );
};

export default PrimaryLayout;
